import React from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

function ClientSignatureCard({
  title = "For Client Only - Signature",
  acknowledgedClientSignatory = "",
  setAcknowledgedClientSignatory,
  setValue,
  clearErrors,
  errors,
  acknowledgedBySignatureRef,
  canvasWidth,
  ackHeight,
  saveAndUploadAcknowledgedSignature,
  clearAcknowledgedSignature,
  isLoading,
  isTechnician,
  clientSignatureUploadedAt,

  readOnly = false,
  showUploadActions = true,
  signatureImageUrl = "",
  representativeLabel = "Client Representative",
}) {
  return (
    <Grid item xs={12}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", textTransform: "uppercase" }}
            >
              {title}
            </Typography>
          }
          sx={{ px: 2, pt: 2, pb: 0 }}
        />

        <CardContent sx={{ px: 2, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={representativeLabel}
                value={acknowledgedClientSignatory || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setAcknowledgedClientSignatory?.(val);
                  setValue?.("acknowledgedClientSignatory", val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  clearErrors?.("acknowledgedClientSignatory");
                }}
                size="small"
                disabled={readOnly}
                error={!!errors?.acknowledgedClientSignatory}
                helperText={errors?.acknowledgedClientSignatory?.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: canvasWidth,
                  height: ackHeight,
                  border: "2px solid #000",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  bgcolor: "background.paper",
                }}
              >
                {readOnly ? (
                  signatureImageUrl ? (
                    <Box
                      component="img"
                      src={signatureImageUrl}
                      alt="Client Signature"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No signature uploaded.
                    </Typography>
                  )
                ) : (
                  <SignatureCanvas
                    ref={acknowledgedBySignatureRef}
                    penColor="black"
                    canvasProps={{
                      width: canvasWidth,
                      height: ackHeight,
                      className: "sigCanvas",
                    }}
                  />
                )}
              </Box>

              {!readOnly && showUploadActions && (
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={saveAndUploadAcknowledgedSignature}
                    disabled={isLoading}
                  >
                    Upload
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={clearAcknowledgedSignature}
                    disabled={isLoading}
                  >
                    Clear
                  </Button>
                </Box>
              )}

              {!readOnly &&
                (errors?.acknowledgedUploaded || errors?.acknowledgedSignature) && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errors?.acknowledgedUploaded?.message ||
                      errors?.acknowledgedSignature?.message}
                  </FormHelperText>
                )}

              {!isTechnician && clientSignatureUploadedAt && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    border: "1px solid #43a047",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#2e7d32", fontWeight: 600 }}
                  >
                    Client Signature Uploaded At:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "#1b5e20", mt: 0.5 }}
                  >
                    {new Date(clientSignatureUploadedAt).toLocaleString("en-US", {
                      month: "short",
                      day: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                    })}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: "#388e3c", mt: 0.5, display: "block" }}
                  >
                    This timestamp is saved and cannot be changed.
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default ClientSignatureCard;